import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { SxProps, Typography } from "@mui/material";
import { TabsStyles } from "./BasicTabsStyles";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface MuiTabsProps {
  tabs: Array<{
    label: string;
    imageUrl?: string;
    activeImageUrl?: string;
    secondaryLabel?: string | number;
    content: React.ReactNode;
  }>;
  additionalLabelClass?: SxProps<Theme>;
  activeTab?: string; // External control for the active tab
  onTabChange?: (label: string) => void; // Callback for tab change
}

const StyledTabs = styled(Tabs)({
  display: "flex",
  "& .MuiTabs-flexContainer": {
    gap: "30px",
    "& .MuiTab-root": {
      padding: "10px 20px !important",
    },
  },
});
const StyledTab = styled(Tab)({
  fontSize: "var(--font-size-16)",
  fontWeight: "var(--font-weight-500)",
  lineHeight: 0,
  textAlign: "left",
  textTransform: "none",
  color: "var(--text-black)",
  "&.Mui-selected": {
    color: `var(--black) !important`,
    fontWeight: 700,
  },
});
const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`basic-tabs-panel-${index}`}
      data-test-id={`test-basic-tabs-panel-${index}`}
      aria-labelledby={`basic-tabs-${index}`}
      {...other}
    >
      {value === index && (
        <Box 
          id={`basic-tabs-panel-content-${index}`}
          data-test-id={`test-basic-tabs-panel-content-${index}`}
          aria-label={`Tab panel content ${index + 1}`}
          sx={{ paddingTop: "0px" }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `basic-tabs-${index}`,
    "aria-controls": `basic-tabs-panel-${index}`,
    "data-test-id": `test-basic-tabs-${index}`,
    "aria-label": `Tab ${index + 1}`,
  };
};

const BasicTabs = ({
  tabs,
  additionalLabelClass,
  activeTab,
  onTabChange,
}: MuiTabsProps) => {
  const [internalValue, setInternalValue] = React.useState(0);

  // Determine active tab index
  const activeIndex = React.useMemo(() => {
    return tabs.findIndex((tab) => tab.label === activeTab);
  }, [tabs, activeTab]);

  const value = activeTab !== undefined ? activeIndex : internalValue;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    const newTabLabel = tabs[newValue].label;

    if (onTabChange) {
      onTabChange(newTabLabel); // Notify parent component of the change
    } else {
      setInternalValue(newValue); // Handle internally if no external control
    }
  };

  if (!tabs) {
    return null;
  }

  return (
    <Box 
      id="basic-tabs-container"
      data-test-id="test-basic-tabs-container"
      aria-label="Basic tabs container"
      sx={TabsStyles.tabContainer}
    >
      <Box 
        id="basic-tabs-box"
        data-test-id="test-basic-tabs-box"
        aria-label="Tabs navigation box"
        sx={TabsStyles.tabBox}
      >
        <StyledTabs
          id="basic-tabs-navigation"
          data-test-id="test-basic-tabs-navigation"
          aria-label="Basic tabs navigation"
          TabIndicatorProps={{
            style: {
              backgroundColor: "var(--black)",
              border: `2px solid var(--black)`,
            },
          }}
          value={value}
          onChange={handleChange}
        >
          {tabs.map((tab, index) => (
            <StyledTab
              key={index}
              label={
                tab.imageUrl ? (
                  <Box 
                    id={`basic-tabs-content-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                    data-test-id={`test-basic-tabs-content-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                    aria-label={`Tab content for ${tab.label}`}
                    sx={TabsStyles.tabs}
                  >
                    <Box 
                      id={`basic-tabs-icon-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      data-test-id={`test-basic-tabs-icon-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-label={`Icon container for ${tab.label}`}
                      sx={TabsStyles.icon}
                    >
                      <img
                        src={value === index ? tab.activeImageUrl : tab.imageUrl}
                        alt={`Icon for ${tab.label}`}
                        id={`basic-tabs-image-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        data-test-id={`test-basic-tabs-image-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-label={`${tab.label} icon`}
                      />
                    </Box>
                    {tab.secondaryLabel && (
                      <Typography 
                        id={`basic-tabs-secondary-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        data-test-id={`test-basic-tabs-secondary-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-label={`Secondary label for ${tab.label}`}
                        sx={TabsStyles.secondaryLabel}
                      >
                        {tab.secondaryLabel}
                      </Typography>
                    )}
                    <Typography 
                      id={`basic-tabs-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      data-test-id={`test-basic-tabs-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-label={`Label for ${tab.label}`}
                      sx={TabsStyles.label}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                ) : (
                  <Box 
                    id={`basic-tabs-content-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                    data-test-id={`test-basic-tabs-content-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                    aria-label={`Tab content for ${tab.label}`}
                    sx={TabsStyles.tabs}
                  >
                    {tab.secondaryLabel && (
                      <Typography 
                        id={`basic-tabs-secondary-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        data-test-id={`test-basic-tabs-secondary-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-label={`Secondary label for ${tab.label}`}
                        sx={TabsStyles.secondaryLabel}
                      >
                        {tab.secondaryLabel}
                      </Typography>
                    )}
                    <Typography
                      id={`basic-tabs-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      data-test-id={`test-basic-tabs-label-${tab.label.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-label={`Label for ${tab.label}`}
                      sx={Object.assign(
                        {},
                        TabsStyles.label,
                        additionalLabelClass
                      )}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                )
              }
              {...a11yProps(index)}
            />
          ))}
        </StyledTabs>
      </Box>
      {tabs.map((tab, index) => (
        <CustomTabPanel key={index} value={value} index={index}>
          {tab.content}
        </CustomTabPanel>
      ))}
    </Box>
  );
};

export default BasicTabs;
